import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { auth } from '@/api/endpoints'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Err } from '@/components/Err'
import { AuthCard } from './Card'

// 메일의 인증 링크가 여는 화면(F-33). 비밀번호는 신청할 때 정했으므로(F-120) 열자마자 토큰만 보내 이메일을 확인한다.
// 그 전에 만든 신청은 비밀번호가 없어 password_required 로 답하고, 그때만 비밀번호를 두 번 받아 함께 보낸다.

const reasonOf = (err: unknown) =>
  err && typeof err === 'object' && 'reason' in err ? (err as { reason?: string }).reason : undefined

export default function VerifyEmail() {
  const [params] = useSearchParams()
  const token = params.get('t') ?? ''
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [state, setState] = useState<'checking' | 'form' | 'ok' | 'bad'>(token ? 'checking' : 'bad')
  const [error, setError] = useState<unknown>(null)
  // 토큰은 한 번만 쓸 수 있다 — 개발 모드의 이중 effect 가 두 번 보내 둘째가 bad_token 이 되지 않게.
  const sent = useRef(false)
  const same = next.length > 0 && next === again

  useEffect(() => {
    if (!token || sent.current) return
    sent.current = true
    auth
      .verify(token)
      .then(() => setState('ok'))
      .catch((err: unknown) => {
        const reason = reasonOf(err)
        if (reason === 'password_required') setState('form')
        else if (reason === 'bad_token') setState('bad')
        else {
          setError(err)
          setState('form')
        }
      })
  }, [token])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await auth.verify(token, next)
      setNext('')
      setAgain('')
      setState('ok')
    } catch (err) {
      if (reasonOf(err) === 'bad_token') setState('bad')
      else setError(err)
    } finally {
      setBusy(false)
    }
  }
  return (
    <AuthCard title="이메일 확인" onSubmit={submit}>
      {state === 'checking' ? <p className="text-[13px] text-muted-foreground">확인하는 중</p> : null}
      {state === 'ok' ? (
        <>
          <p className="text-[13px]">이메일이 확인되었습니다. 관리자가 승인하면 로그인할 수 있습니다.</p>
          <Button asChild variant="outline"><Link to="/">로그인</Link></Button>
        </>
      ) : null}
      {state === 'bad' ? (
        <>
          <p className="text-[13px] text-danger-ink">링크가 유효하지 않거나 만료되었습니다.</p>
          <Button asChild variant="outline"><Link to="/">로그인</Link></Button>
        </>
      ) : null}
      {state === 'form' ? (
        <>
          <Input type="password" placeholder="비밀번호" autoComplete="new-password" minLength={8} value={next} disabled={busy} onChange={(e) => setNext(e.target.value)} />
          <Input type="password" placeholder="비밀번호 확인" autoComplete="new-password" value={again} disabled={busy} onChange={(e) => setAgain(e.target.value)} />
          {again.length > 0 && !same ? <p className="text-[12px] text-danger-ink">비밀번호가 서로 다릅니다.</p> : null}
          <Button type="submit" disabled={busy || next.length < 8 || !same} className="bg-brand hover:bg-brand-hover">
            {busy ? '확인 중' : '확인'}
          </Button>
          <Err error={error} />
          <Link to="/" className="text-center text-[12px] text-muted-foreground hover:text-foreground hover:underline">로그인</Link>
        </>
      ) : null}
    </AuthCard>
  )
}
